import clsx from 'clsx';
import { Button } from 'components/Button/Button';
import { ButtonSpinner } from 'components/ButtonSpinner/ButtonSpinner';
import useOtpCountdown from 'components/Otp/useOtpCountdown';
import { OtpInput } from 'components/OtpInput/OtpInput';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppLanguage from 'hooks/useAppLanguage';
import usePageConfig from 'hooks/usePageConfig';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { setSendSmsOtpError } from 'store/otp/otp-slice';
import { RootState } from 'store/reducer';
import colors from 'styles/colors';
import { Process } from 'types/otp';
import { otpInterval, signupOtpSkipDelay } from 'utils/constants';
import messages from './messages';

const EnterOtp = ({
  title,
  subtitle,
  onChangeOtp,
  resendOtp,
  BottomButtonText,
  darkText,
  hasError,
  otpMessage,
  type,
  isLoading,
  errorMessage,
  hasSkip,
  onSkip,
  process,
}: {
  title: string;
  subtitle: string;
  onChangeOtp: (value: string) => void;
  resendOtp: () => void;
  darkText?: boolean;
  BottomButtonText?: string;
  hasError?: boolean;
  otpMessage?: string;
  type?: string;
  isLoading?: boolean;
  errorMessage?: string;
  hasSkip?: boolean;
  onSkip?: () => void;
  process: Process;
}) => {
  const {
    emailOtpTimestamps,
    loading,
    smsOtpTimestamps,
    initiateSmsOtpResponse,
  } = useSelector((rootState: RootState) => rootState.otp);

  const language = useAppLanguage();
  const pageConfig = usePageConfig();
  const timestamp =
    type === 'SMS' ? smsOtpTimestamps[process] : emailOtpTimestamps[process];
  const otpCountdown = useOtpCountdown(timestamp, otpInterval);
  const dispatch = useAppDispatch();

  const viewMessages = messages[language].enterOptView;

  const [canSkip, setCanSkip] = useState<boolean>(false);
  const disableResendOtp =
    otpCountdown > 0 || isLoading || !initiateSmsOtpResponse?.success;
  const resendButtonText =
    otpCountdown === 0
      ? BottomButtonText || viewMessages.resend
      : viewMessages.skipCountdown(otpCountdown);

  useEffect(() => {
    const clearReduxErrorState = () => {
      dispatch(setSendSmsOtpError(false));
    };

    return () => clearReduxErrorState();
  }, []);

  useEffect(() => {
    setTimeout(() => setCanSkip(true), signupOtpSkipDelay);
  }, []);

  return (
    <div
      className={clsx('lg:w-512 flex flex-col items-center', {
        'text-off-black': darkText,
      })}>
      <div className="lg:mt-6 p-4 lg:p-0 mb-2">
        <h2
          className={`font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide ${
            darkText ? 'text-black-navy' : 'text-white'
          }`}>
          {title}
        </h2>
        <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
          {subtitle}
        </p>
      </div>
      <div className="bg-background rounded-lg flex flex-col items-center py-8 lg:p-8">
        {!darkText && (
          <div className="mb-6 font-normal text-white">
            {otpMessage ||
              viewMessages.enterCode(type === 'EMAIL' ? 'Email' : 'SMS')}
          </div>
        )}
        <OtpInput
          isNumberInput
          length={6}
          inputClassName="bg-input-background text-input-text otpInput"
          onChangeOtp={onChangeOtp}
          warning={hasError}
        />
        {!initiateSmsOtpResponse?.success && !isLoading && !darkText && (
          <div className="m-4 justify-center text-center text-error">
            {viewMessages?.otpResendErrorMessage('support@altify.app')}
          </div>
        )}

        <div className="px-4 lg:px-0 flex flex-col items-center">
          <div className="min-h-50 mt-4">
            {hasError && !isLoading && (
              <div className="text-error text-center">
                {errorMessage ||
                  viewMessages.errorMessage(type?.toLowerCase() || '')}
              </div>
            )}
            {(isLoading || loading) && (
              <div className="mt-4">
                <ButtonSpinner />
              </div>
            )}
          </div>
          <div className={clsx('flex justify-center')}>
            {hasSkip ? (
              <div className="mt-4 flex flex-col w-full">
                <Button
                  onClick={() => resendOtp()}
                  disabled={disableResendOtp}
                  main
                  fluid>
                  {resendButtonText}
                </Button>

                <button
                  onClick={onSkip}
                  type="button"
                  className={clsx(
                    'mt-4 font-semibold',
                    darkText ? 'text-vivid-blue' : 'text-white',
                    {
                      'cursor-auto opacity-50': !canSkip,
                    },
                  )}
                  disabled={!canSkip}>
                  <div>Skip</div>
                </button>
                {!initiateSmsOtpResponse?.success && !isLoading && (
                  <div className="m-4 justify-center text-center text-error">
                    {viewMessages?.otpResendErrorMessage('support@altify.app')}
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={() => resendOtp()}
                disabled={disableResendOtp}
                main
                fluid>
                {resendButtonText}
              </Button>
            )}
          </div>
          <div
            className={clsx('mt-5 border-t-2 sm:mx-auto sm:w-full flex flex-col justify-center items-center', {
              'border-blue-grey': !darkText,
              'border-sky-500': darkText,
            })}>
            <div
              className={`mt-6 mb-4 ${
                darkText ? 'text-black-navy' : 'text-white'
              }`}>
              {viewMessages.havingTroubleTitle}
            </div>
            <p className={'text-center'}
              dangerouslySetInnerHTML={{
                __html: viewMessages.havingTroubleBody(
                  darkText ? colors.navy : colors.white,
                  pageConfig?.app?.supportEmail as string,
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterOtp;
